// js/AdminBoard/CustomerSupportBoard/DeviceDetail/Tabs/SipInfoTab.jsx
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import CheckIcon from "@mui/icons-material/CheckRounded";
import XIcon from "@mui/icons-material/CloseRounded";
import EditIcon from "@mui/icons-material/EditRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import {
    fetchSipForDevice,
    createSipForDevice,
    updateSipAccount,
} from "js/Api/index";

const SIP_FIELDS = [
    {
        key: "username",
        label: "SIP Username",
        monospace: false,
        example: "Typically the PBX extension, e.g. 1234",
    },
    {
        key: "password",
        label: "SIP Password",
        monospace: false,
        mask: true,
    },
    {
        key: "domain",
        label: "SIP Domain",
        monospace: true,
        example: "carewindow.crazypbx.net.au;transport=tcp",
    },
    {
        key: "phoneNumber",
        label: "SIP Phone Number",
        monospace: true,
        example: "+61748494659",
    },
];

const EMPTY_SIP = SIP_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

function SipFieldRow({
    field,
    value,
    isEditing,
    editValue,
    setEditValue,
    onStartEdit,
    onCancel,
    onSave,
    saving,
    revealed,
    onToggleReveal,
}) {
    const displayValue = field.mask && value && !revealed ? "••••••••" : value;

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                padding: "8px 0",
            }}
        >
            <span
                style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    color: "#666",
                }}
            >
                {field.label}
            </span>

            {isEditing ? (
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                            type="text"
                            autoFocus
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            disabled={saving}
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            style={{
                                flex: 1,
                                padding: "6px 10px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                fontSize: "14px",
                                outline: "none",
                                fontFamily: field.monospace ? "monospace" : "inherit",
                                minWidth: 0,
                            }}
                        />
                        <div style={{ display: "flex", gap: 4 }}>
                            <CheckIcon
                                onClick={saving ? undefined : onSave}
                                sx={{
                                    cursor: saving ? "not-allowed" : "pointer",
                                    color: saving ? "#ccc" : "#4CAF50",
                                    fontSize: "1.2rem",
                                }}
                            />
                            <XIcon
                                onClick={saving ? undefined : onCancel}
                                sx={{
                                    cursor: saving ? "not-allowed" : "pointer",
                                    color: saving ? "#ccc" : "#FF5252",
                                    fontSize: "1.2rem",
                                }}
                            />
                        </div>
                    </div>
                    {field.example && (
                        <div
                            style={{
                                marginTop: 4,
                                fontSize: 12,
                                color: "#888",
                                fontStyle: "italic",
                                fontFamily: field.monospace ? "monospace" : "inherit",
                            }}
                        >
                            Example: {field.example}
                        </div>
                    )}
                </div>
            ) : value ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                        style={{
                            fontSize: 14,
                            fontFamily: field.monospace ? "monospace" : "inherit",
                            wordBreak: "break-all",
                        }}
                    >
                        {displayValue}
                    </span>
                    <EditIcon
                        onClick={onStartEdit}
                        sx={{
                            fontSize: "1rem",
                            cursor: "pointer",
                            color: "#666",
                            flexShrink: 0,
                        }}
                    />
                    {field.mask && (
                        <span
                            onClick={onToggleReveal}
                            title={revealed ? "Hide" : "Show"}
                            style={{
                                cursor: "pointer",
                                color: "#666",
                                display: "inline-flex",
                                alignItems: "center",
                            }}
                        >
                            {revealed ? (
                                <VisibilityOffIcon sx={{ fontSize: "1rem" }} />
                            ) : (
                                <VisibilityIcon sx={{ fontSize: "1rem" }} />
                            )}
                        </span>
                    )}
                </div>
            ) : (
                <button
                    type="button"
                    onClick={onStartEdit}
                    style={{
                        alignSelf: "flex-start",
                        background: "transparent",
                        border: "1px dashed #c44",
                        borderRadius: 16,
                        color: "#c44",
                        padding: "4px 12px",
                        fontSize: 13,
                        cursor: "pointer",
                    }}
                >
                    + {field.label}
                </button>
            )}
        </div>
    );
}

// API may return an array (multiple SIP accounts per device) or a single object.
// v1 assumption: at most one account per device, so we take the first.
function pickFirstAccount(data) {
    if (!data) return null;
    if (Array.isArray(data)) return data[0] || null;
    return data;
}

function pickSipFields(account) {
    if (!account) return { ...EMPTY_SIP };
    return SIP_FIELDS.reduce(
        (acc, f) => ({ ...acc, [f.key]: account[f.key] || "" }),
        {}
    );
}

function SipInfoTab({ device }) {
    const [loading, setLoading] = useState(false);
    const [sipAccountId, setSipAccountId] = useState(null);
    const [sipData, setSipData] = useState(EMPTY_SIP);
    const [editingField, setEditingField] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [saving, setSaving] = useState(false);
    const [revealedFields, setRevealedFields] = useState({});

    const deviceId = device?.id;
    const isCareWindowDevice = device?.deviceType === "android-carewindow";

    useEffect(() => {
        let cancelled = false;
        if (!deviceId || !isCareWindowDevice) return undefined;
        setLoading(true);
        fetchSipForDevice(deviceId)
            .then((res) => {
                if (cancelled) return;
                if (res.success) {
                    const account = pickFirstAccount(res.data);
                    setSipAccountId(account?.id || null);
                    setSipData(pickSipFields(account));
                } else {
                    toast.error(res.error || "Failed to load SIP settings");
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [deviceId, isCareWindowDevice]);

    const startEdit = (fieldKey, currentValue) => {
        setEditingField(fieldKey);
        setEditValue(currentValue || "");
    };

    const cancelEdit = () => {
        setEditingField(null);
        setEditValue("");
    };

    const saveEdit = async () => {
        if (!editingField) return;
        if (!isCareWindowDevice) return;
        setSaving(true);

        const normalized = (editValue ?? "").trim();
        const body = { ...sipData, [editingField]: normalized };

        const res = sipAccountId
            ? await updateSipAccount(sipAccountId, body)
            : await createSipForDevice(deviceId, body);

        setSaving(false);

        if (res.success) {
            // POST returns the new account; capture its id for subsequent PUTs
            if (!sipAccountId) {
                const newAccount = pickFirstAccount(res.data);
                if (newAccount?.id) {
                    setSipAccountId(newAccount.id);
                }
            }
            setSipData(body);
            toast.success(res.message || "SIP setting updated");
            setEditingField(null);
            setEditValue("");
        } else {
            toast.error(res.error || "Failed to update SIP setting");
        }
    };

    if (!deviceId) {
        return (
            <div className="padding_infor_info" style={{ paddingTop: 20 }}>
                <p>No device selected.</p>
            </div>
        );
    }

    if (!isCareWindowDevice) {
        return (
            <div className="padding_infor_info" style={{ paddingTop: 20 }}>
                <p style={{ color: "#666" }}>
                    SIP settings are only available for CareWindow devices.
                </p>
            </div>
        );
    }

    return (
        <div className="padding_infor_info" style={{ paddingTop: 20 }}>
            <h5 style={{ marginTop: 0, marginBottom: 15 }}>SIP Account Details</h5>

            {loading ? (
                <div style={{ padding: "20px 0", textAlign: "center" }}>
                    <i
                        className="fa fa-spinner fa-spin"
                        style={{ fontSize: 24, marginRight: 8, color: "#007bff" }}
                    />
                    <p style={{ marginTop: 10, color: "#666" }}>Loading SIP settings...</p>
                </div>
            ) : (
                <div
                    style={{
                        border: "1px solid #eee",
                        borderRadius: 6,
                        backgroundColor: "#fafafa",
                        padding: 15,
                    }}
                >
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                            gap: "8px 30px",
                        }}
                    >
                        {SIP_FIELDS.map((field) => (
                            <SipFieldRow
                                key={field.key}
                                field={field}
                                value={sipData[field.key]}
                                isEditing={editingField === field.key}
                                editValue={editValue}
                                setEditValue={setEditValue}
                                onStartEdit={() => startEdit(field.key, sipData[field.key])}
                                onCancel={cancelEdit}
                                onSave={saveEdit}
                                saving={saving}
                                revealed={!!revealedFields[field.key]}
                                onToggleReveal={() =>
                                    setRevealedFields((prev) => ({
                                        ...prev,
                                        [field.key]: !prev[field.key],
                                    }))
                                }
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default SipInfoTab;
